import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export type MainNavigationVersion = "v1" | "v2";

const SETTINGS_KEY = "main_navigation_version";
const CHANGE_EVENT = "main-navigation-version-change";

const isVersion = (value: unknown): value is MainNavigationVersion =>
  value === "v1" || value === "v2";

const CACHE_KEY = "main_navigation_version_cache";

const readCachedVersion = (): MainNavigationVersion | null => {
  try {
    const raw = window.sessionStorage.getItem(CACHE_KEY);
    return isVersion(raw) ? raw : null;
  } catch {
    return null;
  }
};

const cacheVersion = (version: MainNavigationVersion) => {
  try {
    window.sessionStorage.setItem(CACHE_KEY, version);
  } catch {
    // ignore storage errors
  }
};

export const useMainNavigationVersion = () => {
  const [version, setVersion] = useState<MainNavigationVersion>("v1");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const cached = readCachedVersion();
    if (cached) {
      setVersion(cached);
      setLoading(false);
    }
    const { data } = await supabase
      .from("site_settings")
      .select("value")
      .eq("key", SETTINGS_KEY)
      .maybeSingle();
    const value = data?.value as unknown;
    const resolved = isVersion(value) ? value : "v1";
    setVersion(resolved);
    cacheVersion(resolved);
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
    const onChange = (event: Event) => {
      const value = (event as CustomEvent<unknown>).detail;
      if (isVersion(value)) setVersion(value);
    };
    window.addEventListener(CHANGE_EVENT, onChange);
    return () => window.removeEventListener(CHANGE_EVENT, onChange);
  }, [load]);

  return { version, loading, refetch: load };
};

export const saveMainNavigationVersion = async (version: MainNavigationVersion) => {
  const { error } = await supabase.from("site_settings").upsert({
    key: SETTINGS_KEY,
    value: version as never,
    updated_at: new Date().toISOString(),
  });
  if (error) throw error;
  cacheVersion(version);
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: version }));
};